﻿import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Chip,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Refresh } from "@material-ui/icons";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";

const dayLabels = [
  { key: "monday" },
  { key: "tuesday" },
  { key: "wednesday" },
  { key: "thursday" },
  { key: "friday" },
  { key: "saturday" },
  { key: "sunday" },
];

const defaultBusinessHours = [
  { day: "monday", enabled: true, start: "08:00", end: "18:00" },
  { day: "tuesday", enabled: true, start: "08:00", end: "18:00" },
  { day: "wednesday", enabled: true, start: "08:00", end: "18:00" },
  { day: "thursday", enabled: true, start: "08:00", end: "18:00" },
  { day: "friday", enabled: true, start: "08:00", end: "18:00" },
  { day: "saturday", enabled: false, start: "08:00", end: "12:00" },
  { day: "sunday", enabled: false, start: "08:00", end: "12:00" },
];

const useStyles = makeStyles((theme) => ({
  scrollArea: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    ...theme.scrollbarStyles,
  },
  sectionPaper: {
    padding: theme.spacing(2.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 700,
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  settingsGrid: {
    alignItems: "flex-start",
  },
  hoursTable: {
    "& td, & th": {
      whiteSpace: "nowrap",
    },
  },
  connectionCard: {
    padding: theme.spacing(2),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)"
        : "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  connectionTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  connectionName: {
    fontSize: 17,
    fontWeight: 700,
  },
  connectionMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  toggleRow: {
    display: "flex",
    gap: theme.spacing(3),
    flexWrap: "wrap",
  },
  connectionActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  queuesHint: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  licenseStatusCard: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  licenseStatusMeta: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  licenseReason: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
}));

const parseBusinessHours = (rawValue) => {
  if (!rawValue) {
    return defaultBusinessHours;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return defaultBusinessHours;
    }

    return dayLabels.map((day) => {
      const found = parsed.find((item) => item.day === day.key);
      return found || defaultBusinessHours.find((item) => item.day === day.key);
    });
  } catch (error) {
    return defaultBusinessHours;
  }
};

const CompanyPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState([]);
  const [awayMinutesDraft, setAwayMinutesDraft] = useState("5");
  const [ticketSubjectRequiredDraft, setTicketSubjectRequiredDraft] = useState(false);
  const [autoResumeSlaDraft, setAutoResumeSlaDraft] = useState(false);
  const [userCanSeeQueueTicketsDraft, setUserCanSeeQueueTicketsDraft] = useState(true);
  const [loadPreviousTicketMessagesDraft, setLoadPreviousTicketMessagesDraft] = useState(false);
  const [previousTicketMessagesPageSizeDraft, setPreviousTicketMessagesPageSizeDraft] = useState("20");
  const [allowUsersClosePendingWithoutAcceptDraft, setAllowUsersClosePendingWithoutAcceptDraft] = useState(false);
  const [allowPendingTicketPreviewDraft, setAllowPendingTicketPreviewDraft] = useState(false);
  const [allowCallRecordingPlaybackDraft, setAllowCallRecordingPlaybackDraft] = useState(false);
  const [allowContactBlockingFeature, setAllowContactBlockingFeature] = useState(false);
  const [businessHours, setBusinessHours] = useState(defaultBusinessHours);
  const [outOfHoursMessageDraft, setOutOfHoursMessageDraft] = useState("");
  const [outOfHoursActionDraft, setOutOfHoursActionDraft] = useState("keep_pending");
  const [blockedContactMessageDraft, setBlockedContactMessageDraft] = useState("");
  const [blockedContactActionDraft, setBlockedContactActionDraft] = useState("close_ticket");
  const [blockedContactCloseSubjectIdDraft, setBlockedContactCloseSubjectIdDraft] = useState("");
  const [ticketSubjectsOptions, setTicketSubjectsOptions] = useState([]);
  const [licenseStatus, setLicenseStatus] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        settingsResponse,
        licenseStatusResponse,
        companyPreferencesResponse,
        ticketSubjectsResponse
      ] = await Promise.all([
        api.get("/settings"),
        api.get("/licenses/status"),
        api.get("/companies/current/preferences"),
        api.get("/ticket-subjects/options"),
      ]);

      const settingsData = Array.isArray(settingsResponse.data)
        ? settingsResponse.data
        : [];
      const ticketSubjectsData = Array.isArray(ticketSubjectsResponse.data)
        ? ticketSubjectsResponse.data
        : [];
      const nextLicenseStatus =
        licenseStatusResponse && typeof licenseStatusResponse.data === "object"
          ? licenseStatusResponse.data
          : null;

      setSettings(settingsData);
      setAwayMinutesDraft(
        settingsData.find((item) => item.key === "userAwayMinutes")?.value || "5"
      );
      setTicketSubjectRequiredDraft(
        (settingsData.find((item) => item.key === "ticketSubjectRequired")?.value ||
          "disabled") === "enabled"
      );
      setAutoResumeSlaDraft(
        (settingsData.find((item) => item.key === "autoResumeSlaOnCustomerReply")?.value ||
          "disabled") === "enabled"
      );
      setUserCanSeeQueueTicketsDraft(
        (settingsData.find((item) => item.key === "userCanSeeQueueTickets")?.value ||
          "enabled") === "enabled"
      );
      setLoadPreviousTicketMessagesDraft(
        (settingsData.find((item) => item.key === "loadPreviousTicketMessages")?.value ||
          "disabled") === "enabled"
      );
      setPreviousTicketMessagesPageSizeDraft(
        String(
          settingsData.find(
            (item) => item.key === "previousTicketMessagesPageSize"
          )?.value || "20"
        )
      );
      setAllowUsersClosePendingWithoutAcceptDraft(
        (settingsData.find((item) => item.key === "allowUsersClosePendingWithoutAccept")?.value ||
          "disabled") === "enabled"
      );
      setAllowPendingTicketPreviewDraft(
        Boolean(companyPreferencesResponse.data?.allowPendingTicketPreview)
      );
      setAllowCallRecordingPlaybackDraft(
        Boolean(companyPreferencesResponse.data?.allowCallRecordingPlayback)
      );
      setAllowContactBlockingFeature(
        Boolean(companyPreferencesResponse.data?.allowContactBlocking)
      );
      setBusinessHours(
        parseBusinessHours(
          settingsData.find((item) => item.key === "companyBusinessHours")?.value
        )
      );
      setOutOfHoursMessageDraft(
        settingsData.find((item) => item.key === "companyOutOfHoursMessage")?.value || ""
      );
      setOutOfHoursActionDraft(
        settingsData.find((item) => item.key === "companyOutOfHoursAction")?.value ||
          "keep_pending"
      );
      setBlockedContactMessageDraft(
        settingsData.find((item) => item.key === "blockedContactMessage")?.value ||
          ""
      );
      setBlockedContactActionDraft(
        settingsData.find((item) => item.key === "blockedContactAction")?.value ||
          "close_ticket"
      );
      setBlockedContactCloseSubjectIdDraft(
        settingsData.find((item) => item.key === "blockedContactCloseSubjectId")?.value ||
          ""
      );
      setTicketSubjectsOptions(ticketSubjectsData);
      setLicenseStatus(nextLicenseStatus);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveSetting = async (key, value) => {
    const { data } = await api.put(`/settings/${key}`, { value });
    setSettings((prevSettings) => {
      const nextSettings = [...prevSettings];
      const index = nextSettings.findIndex((item) => item.key === key);

      if (index >= 0) {
        nextSettings[index] = data;
        return nextSettings;
      }

      return [...nextSettings, data];
    });
  };

  const handleBusinessHourChange = (dayKey, field, value) => {
    setBusinessHours((current) =>
      current.map((item) =>
        item.day === dayKey ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSaveCompanyOperation = async () => {
    try {
      await Promise.all([
        handleSaveSetting(
          "userAwayMinutes",
          String(Math.max(1, Number(awayMinutesDraft) || 5))
        ),
        handleSaveSetting(
          "ticketSubjectRequired",
          ticketSubjectRequiredDraft ? "enabled" : "disabled"
        ),
        handleSaveSetting(
          "autoResumeSlaOnCustomerReply",
          autoResumeSlaDraft ? "enabled" : "disabled"
        ),
        handleSaveSetting(
          "userCanSeeQueueTickets",
          userCanSeeQueueTicketsDraft ? "enabled" : "disabled"
        ),
        handleSaveSetting(
          "loadPreviousTicketMessages",
          loadPreviousTicketMessagesDraft ? "enabled" : "disabled"
        ),
        // Limites iguais aos do backend (10 a 100): o servidor tambem recorta,
        // mas deixar o campo aceitar 5000 e so prometer o que nao acontece.
        handleSaveSetting(
          "previousTicketMessagesPageSize",
          String(
            Math.min(
              100,
              Math.max(10, Number(previousTicketMessagesPageSizeDraft) || 20)
            )
          )
        ),
        handleSaveSetting(
          "allowUsersClosePendingWithoutAccept",
          allowUsersClosePendingWithoutAcceptDraft ? "enabled" : "disabled"
        ),
        api.put("/companies/current/preferences", {
          allowPendingTicketPreview: allowPendingTicketPreviewDraft,
          allowCallRecordingPlayback: allowCallRecordingPlaybackDraft,
        })
      ]);
      await loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const handleSaveBusinessHours = async () => {
    try {
      await Promise.all([
        handleSaveSetting("companyBusinessHours", JSON.stringify(businessHours)),
        handleSaveSetting("companyOutOfHoursMessage", outOfHoursMessageDraft),
        handleSaveSetting("companyOutOfHoursAction", outOfHoursActionDraft),
      ]);
      await loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const handleSaveBlockedContactsPolicy = async () => {
    if (!allowContactBlockingFeature) {
      return;
    }

    try {
      await Promise.all([
        handleSaveSetting("blockedContactMessage", blockedContactMessageDraft),
        handleSaveSetting("blockedContactAction", blockedContactActionDraft),
        handleSaveSetting(
          "blockedContactCloseSubjectId",
          blockedContactCloseSubjectIdDraft || ""
        ),
      ]);
      await loadData();
    } catch (error) {
      toastError(error);
    }
  };

  const businessHoursSummary = useMemo(() => {
    const activeDays = businessHours.filter((item) => item.enabled).length;
    return activeDays
      ? i18n.t("companyPage.activeDays", { count: activeDays })
      : i18n.t("companyPage.noConfiguredDay");
  }, [businessHours]);

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("companyPage.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Refresh />}
            onClick={loadData}
            disabled={loading}
          >
            {i18n.t("companyPage.refresh")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {licenseStatus ? (
        <Paper variant="outlined" className={classes.licenseStatusCard}>
          <div className={classes.licenseStatusMeta}>
            <Typography variant="subtitle1">
              {licenseStatus.mode === "central"
                ? "Licença da instalação"
                : "Status da licença"}
            </Typography>
            <Typography variant="body2" className={classes.licenseReason}>
              {licenseStatus.reason
                ? licenseStatus.reason
                : `Validade: ${
                    licenseStatus.expiresAt
                      ? new Date(licenseStatus.expiresAt).toLocaleString("pt-BR")
                      : "não definida"
                  }`}
            </Typography>
          </div>
          <Chip
            label={licenseStatus.valid ? "Licença válida" : "Licença inválida"}
            color={licenseStatus.valid ? "primary" : "secondary"}
          />
        </Paper>
      ) : null}

      <Paper variant="outlined" style={{ marginBottom: 16 }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={i18n.t("companyPage.operationTitle")} />
          <Tab label={i18n.t("companyPage.blockedTitle")} />
          <Tab label={i18n.t("companyPage.businessHoursTitle")} />
        </Tabs>
      </Paper>

      <div className={classes.scrollArea}>
        <Grid container spacing={2}>
          {activeTab === 0 && (
          <Grid item xs={12}>
            <Paper className={classes.sectionPaper} variant="outlined">
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("companyPage.operationTitle")}
                  </Typography>
                  <Typography className={classes.sectionDescription}>
                    {i18n.t("companyPage.operationDescription")}
                  </Typography>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveCompanyOperation}
                >
                  {i18n.t("companyPage.saveOperation")}
                </Button>
              </div>

              <Grid container spacing={2} className={classes.settingsGrid}>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    label={i18n.t("companyPage.awayMinutes")}
                    value={awayMinutesDraft}
                    inputProps={{ min: 1 }}
                    helperText={i18n.t("companyPage.awayMinutesHelp")}
                    onChange={(event) => setAwayMinutesDraft(event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={ticketSubjectRequiredDraft}
                        onChange={(event) =>
                          setTicketSubjectRequiredDraft(event.target.checked)
                        }
                      />
                    }
                    label={i18n.t("companyPage.subjectRequired")}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {i18n.t("companyPage.subjectRequiredHelp")}
                  </Typography>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={allowUsersClosePendingWithoutAcceptDraft}
                          onChange={(event) =>
                            setAllowUsersClosePendingWithoutAcceptDraft(event.target.checked)
                          }
                        />
                      }
                      label={i18n.t("companyPage.pendingCloseByUser")}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("companyPage.pendingCloseByUserHelp")}
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={autoResumeSlaDraft}
                          onChange={(event) =>
                            setAutoResumeSlaDraft(event.target.checked)
                          }
                        />
                      }
                      label={i18n.t("companyPage.autoResumeSla")}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("companyPage.autoResumeSlaHelp")}
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={userCanSeeQueueTicketsDraft}
                          onChange={(event) =>
                            setUserCanSeeQueueTicketsDraft(event.target.checked)
                          }
                        />
                      }
                      label={i18n.t("companyPage.sharedQueue")}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("companyPage.sharedQueueHelp")}
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={loadPreviousTicketMessagesDraft}
                          onChange={(event) =>
                            setLoadPreviousTicketMessagesDraft(event.target.checked)
                          }
                        />
                      }
                      label={i18n.t("companyPage.loadPreviousTicketMessages")}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("companyPage.loadPreviousTicketMessagesHelp")}
                    </Typography>
                    <Box mt={1} maxWidth={280}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        type="number"
                        label={i18n.t("companyPage.previousTicketMessagesPageSize")}
                        value={previousTicketMessagesPageSizeDraft}
                        inputProps={{ min: 10, max: 100 }}
                        helperText={i18n.t(
                          "companyPage.previousTicketMessagesPageSizeHelp"
                        )}
                        onChange={(event) =>
                          setPreviousTicketMessagesPageSizeDraft(event.target.value)
                        }
                      />
                    </Box>
                  </Box>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={allowPendingTicketPreviewDraft}
                          onChange={(event) =>
                            setAllowPendingTicketPreviewDraft(event.target.checked)
                          }
                        />
                      }
                      label={i18n.t("companyPage.previewPending")}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("companyPage.previewPendingHelp")}
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          color="primary"
                          checked={allowCallRecordingPlaybackDraft}
                          onChange={(event) =>
                            setAllowCallRecordingPlaybackDraft(event.target.checked)
                          }
                        />
                      }
                      label={i18n.t("companyPage.recordingsPlayback")}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("companyPage.recordingsPlaybackHelp")}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          )}

          {activeTab === 1 && (
          <Grid item xs={12}>
            <Paper className={classes.sectionPaper} variant="outlined">
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("companyPage.blockedTitle")}
                  </Typography>
                  <Typography className={classes.sectionDescription}>
                    {i18n.t("companyPage.blockedDescription")}
                  </Typography>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveBlockedContactsPolicy}
                  disabled={!allowContactBlockingFeature}
                >
                  {i18n.t("companyPage.saveBlocked")}
                </Button>
              </div>

              {!allowContactBlockingFeature ? (
                <Typography variant="body2" color="textSecondary">
                  {i18n.t("companyPage.featureDisabled")}
                </Typography>
              ) : (
                <>
                  <TextField
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                    label={i18n.t("companyPage.blockedMessage")}
                    value={blockedContactMessageDraft}
                    onChange={(event) =>
                      setBlockedContactMessageDraft(event.target.value)
                    }
                    helperText={i18n.t("companyPage.blockedMessageHelp")}
                  />

                  <FormControl fullWidth variant="outlined">
                    <InputLabel>{i18n.t("companyPage.blockedAction")}</InputLabel>
                    <Select
                      value={blockedContactActionDraft}
                      onChange={(event) =>
                        setBlockedContactActionDraft(event.target.value)
                      }
                      label={i18n.t("companyPage.blockedAction")}
                    >
                      <MenuItem value="close_ticket">{i18n.t("companyPage.closeTicket")}</MenuItem>
                      <MenuItem value="keep_pending">{i18n.t("companyPage.keepPending")}</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl
                    fullWidth
                    variant="outlined"
                    disabled={blockedContactActionDraft !== "close_ticket"}
                  >
                    <InputLabel>{i18n.t("companyPage.blockedCloseSubject")}</InputLabel>
                    <Select
                      value={blockedContactCloseSubjectIdDraft}
                      onChange={(event) =>
                        setBlockedContactCloseSubjectIdDraft(String(event.target.value || ""))
                      }
                      label={i18n.t("companyPage.blockedCloseSubject")}
                    >
                      <MenuItem value="">
                        <em>{i18n.t("companyPage.noSubject")}</em>
                      </MenuItem>
                      {ticketSubjectsOptions.map((subject) => (
                        <MenuItem key={subject.id} value={String(subject.id)}>
                          {subject.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Paper>
          </Grid>
          )}

          {activeTab === 2 && (
          <Grid item xs={12}>
            <Paper className={classes.sectionPaper} variant="outlined">
              <div className={classes.sectionHeader}>
                <div>
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("companyPage.businessHoursTitle")}
                  </Typography>
                  <Typography className={classes.sectionDescription}>
                    {i18n.t("companyPage.businessHoursDescription")}
                  </Typography>
                </div>
                <Box display="flex" gridGap={12} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => history.push("/company-holidays")}
                  >
                    {i18n.t("companyPage.holidays")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveBusinessHours}
                  >
                    {i18n.t("companyPage.saveHours")}
                  </Button>
                </Box>
              </div>

              <Typography variant="body2" color="textSecondary">
                {businessHoursSummary}
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                label={i18n.t("companyPage.outOfHoursMessage")}
                value={outOfHoursMessageDraft}
                onChange={(event) => setOutOfHoursMessageDraft(event.target.value)}
                helperText={i18n.t("companyPage.outOfHoursMessageHelp")}
              />

              <FormControl fullWidth variant="outlined">
                <InputLabel>{i18n.t("companyPage.outOfHoursAction")}</InputLabel>
                <Select
                  value={outOfHoursActionDraft}
                  onChange={(event) => setOutOfHoursActionDraft(event.target.value)}
                  label={i18n.t("companyPage.outOfHoursAction")}
                >
                  <MenuItem value="keep_pending">{i18n.t("companyPage.keepPending")}</MenuItem>
                  <MenuItem value="close_ticket">{i18n.t("companyPage.closeTicket")}</MenuItem>
                </Select>
              </FormControl>

              <Table size="small" className={classes.hoursTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("companyPage.day")}</TableCell>
                    <TableCell>{i18n.t("companyPage.active")}</TableCell>
                    <TableCell>{i18n.t("companyPage.start")}</TableCell>
                    <TableCell>{i18n.t("companyPage.end")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dayLabels.map((day) => {
                    const currentDay =
                      businessHours.find((item) => item.day === day.key) ||
                      defaultBusinessHours.find((item) => item.day === day.key);

                    return (
                      <TableRow key={day.key}>
                        <TableCell>{i18n.t(`weekdays.${day.key}`)}</TableCell>
                        <TableCell>
                          <Switch
                            color="primary"
                            checked={Boolean(currentDay?.enabled)}
                            onChange={(event) =>
                              handleBusinessHourChange(
                                day.key,
                                "enabled",
                                event.target.checked
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            variant="outlined"
                            size="small"
                            value={currentDay?.start || "08:00"}
                            onChange={(event) =>
                              handleBusinessHourChange(
                                day.key,
                                "start",
                                event.target.value
                              )
                            }
                            inputProps={{ step: 300 }}
                            disabled={!currentDay?.enabled}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            variant="outlined"
                            size="small"
                            value={currentDay?.end || "18:00"}
                            onChange={(event) =>
                              handleBusinessHourChange(
                                day.key,
                                "end",
                                event.target.value
                              )
                            }
                            inputProps={{ step: 300 }}
                            disabled={!currentDay?.enabled}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
          )}

        </Grid>
      </div>
    </MainContainer>
  );
};

export default CompanyPage;

